import React, { useMemo, useContext, createContext, useCallback, useEffect, useRef } from 'react';
import useReducerWithMiddleware from '../useReducerWithMiddleware';
import unauthorized from '../unauthorized';
import organizationReducer, { initialOrganizationState, OrganizationState } from './reducer';
import { ORGANIZATION_LIST_REQUESTED, ORGANIZATION_LIST_SUCCESS, ORGANIZATION_LIST_FAILED } from './constants';
import { useSelector } from 'react-redux';
import { profileIdState } from 'selectors';
import { OrganizationListObj } from 'types';
import { useRouteMatch } from 'react-router-dom';
import { organizationClient } from 'clients';

interface OrganizationActions {
    getOrganizations: () => void;
}

interface OrganizationContextState extends Omit<OrganizationState, 'organization'> {
    organization: OrganizationListObj;
}

interface OrganizationContextType extends OrganizationContextState, OrganizationActions {}

export const OrganizationContext = createContext<OrganizationContextType | null>(null);

export const OrganizationProvider: React.FunctionComponent<React.PropsWithChildren<object>> = ({ children }) => {
    const match = useRouteMatch<{ organizationId: string }>('/organization/:organizationId');
    const isMountedRef = useRef(true);
    const [state, dispatch] = useReducerWithMiddleware(
        organizationReducer,
        { ...initialOrganizationState },
        [],
        [unauthorized]
    );
    const profileId = useSelector(profileIdState);
    const organization = useMemo(() => {
        const orgId = match?.params['organizationId'];
        if (!orgId) {
            return null;
        }

        const parsedOrgId = parseInt(orgId, 10);
        return state.organizations?.find((org) => org.pk === parsedOrgId);
    }, [state.organizations, match?.params]);

    const getOrganizations = useCallback(async () => {
        try {
            dispatch({ type: ORGANIZATION_LIST_REQUESTED });
            const organizations = await organizationClient.getOrganizations();
            if (!isMountedRef.current) {
                return;
            }
            dispatch({ type: ORGANIZATION_LIST_SUCCESS, payload: { organizations } });
        } catch (error) {
            if (!isMountedRef.current) {
                return;
            }
            dispatch({ type: ORGANIZATION_LIST_FAILED, payload: { message: 'Cannot retrieve orgs' } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileId]);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!profileId) {
            return;
        }

        getOrganizations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileId]);

    const value = useMemo<OrganizationContextType>(() => {
        return {
            ...state,
            organization,
            getOrganizations,
        } as any;
    }, [state, organization, getOrganizations]);

    return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
};

export const useOrganization = () => {
    const context = useContext(OrganizationContext);
    if (!context) {
        throw new Error('Error: useOrganization should be wrapped by OrganizationProvider.');
    }
    return context;
};
